import React from 'react';
import { 
  Box, 
  Card, 
  CardContent, 
  Typography,
  useTheme,
  Divider,
  Chip
} from '@mui/material';
import { PieChart, Pie, Cell, ResponsiveContainer, Legend, Tooltip } from 'recharts';
import MaleIcon from '@mui/icons-material/Male';
import FemaleIcon from '@mui/icons-material/Female';
import MusicNoteIcon from '@mui/icons-material/MusicNote';
import { BandRecommendation } from '../../../types/BandRecommendations';
import { useBandView } from '../../../contexts/BandViewContext';

interface BandBasicInfoProps {
  band: BandRecommendation;
}

const BandBasicInfo: React.FC<BandBasicInfoProps> = ({ band }) => {
  const theme = useTheme();
  const { viewMode } = useBandView();
  
  const genderData = [
    { 
      name: 'Male', 
      value: band.maleMembers, 
      color: theme.palette.primary.main 
    },
    { 
      name: 'Female', 
      value: band.femaleMembers, 
      color: theme.palette.secondary.main 
    },
  ];

  const CustomizedLegend = () => (
    <Box display="flex" justifyContent="center" alignItems="center">
      <Box display="flex" alignItems="center" mr={2}>
        <MaleIcon style={{ color: theme.palette.primary.main }} />
        <Typography variant="body2" ml={1}>Male ({band.maleMembers})</Typography>
      </Box>
      <Box display="flex" alignItems="center">
        <FemaleIcon style={{ color: theme.palette.secondary.main }} />
        <Typography variant="body2" ml={1}>Female ({band.femaleMembers})</Typography>
      </Box>
    </Box>
  );

  return (
    <Card>
      <CardContent>
        <Typography variant="h6" gutterBottom>Band Demographics</Typography>
        <Box height={200}>
          <ResponsiveContainer width="100%" height="100%">
            <PieChart>
              <Pie
                data={genderData}
                cx="50%"
                cy="50%"
                innerRadius={50}
                outerRadius={70}
                paddingAngle={5}
                dataKey="value"
                nameKey="name"
              >
                {genderData.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={entry.color} />
                ))}
              </Pie>
              <Tooltip 
                formatter={(value: number, name: string) => 
                  [`${value} members`, name]
                }
              />
              <Legend content={<CustomizedLegend />} />
            </PieChart>
          </ResponsiveContainer>
        </Box>
        
        <Box mt={2}>
          <Typography variant="body1" align="center">
            Total Members: {band.maleMembers + band.femaleMembers}
          </Typography>
          <Typography variant="body2" align="center" color="textSecondary">
            {band.genderRatio}
          </Typography>
        </Box>

        {/* Band Members & Instrumentation Section */}
        {band.instrumentation && band.instrumentation.length > 0 && (
          <>
            <Divider sx={{ my: 2 }} />
            <Typography variant="h6" gutterBottom display="flex" alignItems="center">
              <MusicNoteIcon sx={{ mr: 1 }} />
              Band Members
            </Typography>
            <Box>
              {band.instrumentation.map((instrument, index) => (
                <Box key={index} mb={1}>
                  <Typography variant="body1" fontWeight="medium">
                    {instrument.performer?.name || 'Unknown'}
                  </Typography>
                  <Box display="flex" alignItems="center" mt={0.5}>
                    <Chip 
                      label={instrument.instrumentName}
                      size="small"
                      variant="outlined"
                      sx={{ mr: 1 }}
                    />
                    {instrument.withEffects && (
                      <Chip 
                        label="With Effects"
                        size="small"
                        color="secondary"
                        variant="outlined"
                      />
                    )}
                  </Box>
                </Box>
              ))}
            </Box>
          </>
        )}

        {viewMode === 'admin' && band.status === 'pending' && (
          <Typography 
            variant="body2" 
            color="warning.main" 
            align="center" 
            sx={{ mt: 2 }}
          >
            Pending Review
          </Typography>
        )}
      </CardContent>
    </Card>
  );
};

export default BandBasicInfo;